import { ITemplateDocument } from "@rentiohq/shared-frontend/dist/redux/templateDocument/templateDocument.types";
import { getFormattedDate } from "@rentiohq/shared-frontend/dist/reduxV2/chats/chat.utils";
import {
  EInsuranceExternalStatus,
  ERentioMoveServiceStatus,
  ETemplateDocumentStatus,
  IContract,
} from "@rentiohq/shared-frontend/dist/types/contract.types";
import {
  EDocumentPackageStatus,
  IDocumentPackage,
} from "@rentiohq/shared-frontend/dist/types/documentPackage.types";
import {
  EPaymentOrderType,
  IPaymentOrder,
} from "@rentiohq/shared-frontend/dist/types/payment.types";
import { IProperty } from "@rentiohq/shared-frontend/dist/types/property.types";
import {
  ERegistrationBrusselsStatus,
  ERegistrationDocumentStatus,
  ERegistrationDocumentType,
  IRegistration,
} from "@rentiohq/shared-frontend/dist/types/registration.types";
import {
  EBanqupContractStatus,
  ERentDepositProvider,
  ERentDepositStatus,
  IRentDeposit,
} from "@rentiohq/shared-frontend/dist/types/rentDeposit.types";
import { formatDate } from "@rentiohq/shared-frontend/dist/utils/date.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { hasBrusselsRegistration } from "@rentiohq/shared-frontend/dist/utils/property.utils";
import { stringToSnakeCase } from "@rentiohq/shared-frontend/dist/utils/string.utils";
import { ts } from "services";
import { renderSignersTooltip } from "utils/documentPackage.utils";
import {
  renderPOTooltipChecklist,
  renderTooltipChecklist,
} from "utils/tooltip.utils";
import { getPaymentMethodFromRentDepositPo } from "../../ContractCard.utils";
import { EStatus } from "./AdministrationItems.types";

export const getTemplateDocumentContractStatus = ({
  contract,
  contractTemplateDocuments,
}: {
  contract: IContract;
  contractTemplateDocuments?: ITemplateDocument[];
}) => {
  if (contract.manuallyCompletedTemplateDocumentContract) {
    return EStatus.Success;
  }

  const templateDocument = contractTemplateDocuments?.[0];
  const { status = ETemplateDocumentStatus.New } = templateDocument || {};

  switch (status) {
    default:
    case ETemplateDocumentStatus.New:
    case ETemplateDocumentStatus.Failed:
      return EStatus.Error;

    case ETemplateDocumentStatus.Generating:
      return EStatus.Warning;

    case ETemplateDocumentStatus.Generated:
      return EStatus.Success;
  }
};

export const getTemplateDocumentContractTooltip = ({
  contract,
  contractTemplateDocuments,
}: {
  contract: IContract;
  contractTemplateDocuments?: ITemplateDocument[];
}) => {
  if (!contract) {
    return;
  }

  if (contract.manuallyCompletedTemplateDocumentContract) {
    return getLocalizedText(contract.manuallyCompletedTemplateDocumentContract);
  }

  const templateDocument = contractTemplateDocuments?.[0];
  const { status = ETemplateDocumentStatus.New } = templateDocument || {};

  return getLocalizedText(`template_document.status.${status}`.toLowerCase());
};

export const getDocumentPackageStatus = ({
  contract,
  documentPackages,
}: {
  contract: IContract;
  documentPackages?: IDocumentPackage[];
}) => {
  if (contract.manuallyCompletedDocumentSigning) {
    return EStatus.Success;
  }

  if (!documentPackages) {
    return EStatus.None;
  }

  if (documentPackages.length === 0) {
    return EStatus.Error;
  }

  const documentPackage = documentPackages[0];
  switch (documentPackage.status) {
    case EDocumentPackageStatus.Finished:
      return EStatus.Success;

    default:
      return EStatus.Warning;
  }
};

export const getDocumentPackageTooltip = ({
  contract,
  documentPackages,
}: {
  contract: IContract;
  documentPackages?: IDocumentPackage[];
}) => {
  if (contract.manuallyCompletedDocumentSigning) {
    return getLocalizedText(contract.manuallyCompletedDocumentSigning);
  }

  if (!documentPackages) {
    return undefined;
  }

  if (documentPackages.length === 0) {
    return getLocalizedText("status.contracts.empty");
  }

  const documentPackage = documentPackages[0];
  switch (documentPackage.status) {
    case EDocumentPackageStatus.Pending:
    case EDocumentPackageStatus.Finished:
      return renderSignersTooltip(documentPackage);

    default:
      return ts.documentPackagesStatus(documentPackage.status);
  }
};

export const getRentDepositStatus = ({
  contract,
  rentDeposits,
}: {
  contract: IContract;
  rentDeposits?: IRentDeposit[];
}) => {
  if (contract.manuallyCompletedRentDeposit) {
    return EStatus.Success;
  }

  if (!rentDeposits) {
    return EStatus.None;
  }

  if (rentDeposits.length === 0) {
    return EStatus.Error;
  }

  const rentDeposit = rentDeposits[0];

  const isMigrationCase =
    rentDeposit.provider === ERentDepositProvider.Migrated;

  const rentDepositOpeningCompletedStatuses = [
    ERentDepositStatus.Established,
    ERentDepositStatus.ReleaseNew,
    ERentDepositStatus.WaitingForIBAN,
    ERentDepositStatus.WaitingForValidations,
    ERentDepositStatus.ReleaseToSign,
    ERentDepositStatus.ReleaseSigned,
    ERentDepositStatus.ManualVerification,
    ERentDepositStatus.ManualVerificationJudicial,
    ERentDepositStatus.PayingOut,
    ERentDepositStatus.Released,
    ERentDepositStatus.BanqupPendingPayment,
  ];

  const isEstablished =
    isMigrationCase ||
    rentDepositOpeningCompletedStatuses.includes(rentDeposit.status);

  if (isEstablished) {
    return EStatus.Success;
  }

  return EStatus.Warning;
};

export const getRentDepositTooltip = ({
  contract,
  rentDeposits,
}: {
  contract: IContract;
  rentDeposits?: IRentDeposit[];
}) => {
  if (contract.manuallyCompletedRentDeposit) {
    return getLocalizedText(contract.manuallyCompletedRentDeposit);
  }

  if (!rentDeposits) {
    return undefined;
  }

  if (rentDeposits.length === 0) {
    return getLocalizedText("status.rent_deposits.empty");
  }

  const rentDeposit = rentDeposits[0];

  const isMigrationCase =
    rentDeposit.provider === ERentDepositProvider.Migrated;

  const rentDepositOpeningCompletedStatuses = [
    ERentDepositStatus.Established,
    ERentDepositStatus.ReleaseNew,
    ERentDepositStatus.WaitingForIBAN,
    ERentDepositStatus.WaitingForValidations,
    ERentDepositStatus.ReleaseToSign,
    ERentDepositStatus.ReleaseSigned,
    ERentDepositStatus.ManualVerification,
    ERentDepositStatus.ManualVerificationJudicial,
    ERentDepositStatus.PayingOut,
    ERentDepositStatus.Released,
    ERentDepositStatus.BanqupPendingPayment,
  ];

  const isEstablished =
    isMigrationCase ||
    rentDepositOpeningCompletedStatuses.includes(rentDeposit.status);

  if (isEstablished) {
    if (rentDeposit.status === ERentDepositStatus.BanqupPendingPayment) {
      const paymentMethod = getPaymentMethodFromRentDepositPo(rentDeposit);
      return getLocalizedText(
        `follow_up.rent_deposits.filter.status.${rentDeposit.status.toLowerCase()}.label`,
        {
          paymentMethod: paymentMethod
            ? getLocalizedText(`payment_request.method.${paymentMethod}`)
            : getLocalizedText("system.unknown"),
        },
      );
    }

    return ts.followUpRentDepositsFilterStatusLabel({
      extra: { key: ERentDepositStatus.Established.toLowerCase() },
    });
  }

  return ts.followUpRentDepositsFilterStatusLabel({
    extra: { key: rentDeposit.status },
  });
};

export const getRentDepositReleaseStatus = ({
  contract,
  rentDeposits,
}: {
  contract: IContract;
  rentDeposits?: IRentDeposit[];
}) => {
  if (contract.manuallyCompletedEndRentDeposit) {
    return EStatus.Success;
  }

  if (!rentDeposits) {
    return EStatus.Error;
  }

  if (rentDeposits.length === 0) {
    return EStatus.Error;
  }

  const rentDeposit = rentDeposits[0];

  const { status, banqupContractStatus, provider } = rentDeposit;

  const isMigrationCase =
    rentDeposit.provider === ERentDepositProvider.Migrated;

  const releaseStatuses = [
    ERentDepositStatus.ReleaseNew,
    ERentDepositStatus.WaitingForIBAN,
    ERentDepositStatus.WaitingForValidations,
    ERentDepositStatus.ReleaseToSign,
    ERentDepositStatus.ReleaseSigned,
    ERentDepositStatus.ManualVerification,
    ERentDepositStatus.ManualVerificationJudicial,
    ERentDepositStatus.WaitingForVerificationByBanqup,
    ERentDepositStatus.PayingOut,
  ];

  if (isMigrationCase) {
    releaseStatuses.push(ERentDepositStatus.WaitingForVerificationByBanqup);
  }

  const providerIsBanqup = provider !== ERentDepositProvider.MangoPay;

  const releaseFundsTransferred = providerIsBanqup
    ? banqupContractStatus === EBanqupContractStatus.ReleaseFundsTransferred
    : status === ERentDepositStatus.Released;

  if (releaseFundsTransferred) {
    return EStatus.Success;
  }

  if (releaseStatuses.includes(rentDeposit.status)) {
    return EStatus.Warning;
  }

  return EStatus.Error;
};

export const getRentDepositReleaseTooltip = ({
  contract,
  rentDeposits,
}: {
  contract: IContract;
  rentDeposits?: IRentDeposit[];
}) => {
  if (contract.manuallyCompletedEndRentDeposit) {
    return getLocalizedText(contract.manuallyCompletedEndRentDeposit);
  }

  if (!rentDeposits) {
    return getLocalizedText("status.rent_deposits.empty");
  }

  if (rentDeposits.length === 0) {
    return getLocalizedText("status.rent_deposits.empty");
  }

  const rentDeposit = rentDeposits[0];
  const { status, banqupContractStatus, provider } = rentDeposit;

  const isMigrationCase =
    rentDeposit.provider === ERentDepositProvider.Migrated;

  const releaseStatuses = [
    ERentDepositStatus.ReleaseNew,
    ERentDepositStatus.WaitingForIBAN,
    ERentDepositStatus.WaitingForValidations,
    ERentDepositStatus.ReleaseToSign,
    ERentDepositStatus.ReleaseSigned,
    ERentDepositStatus.ManualVerification,
    ERentDepositStatus.ManualVerificationJudicial,
    ERentDepositStatus.WaitingForVerificationByBanqup,
    ERentDepositStatus.PayingOut,
  ];

  if (isMigrationCase) {
    releaseStatuses.push(ERentDepositStatus.WaitingForVerificationByBanqup);
  }

  const providerIsBanqup = provider !== ERentDepositProvider.MangoPay;

  const releaseFundsTransferred = providerIsBanqup
    ? banqupContractStatus === EBanqupContractStatus.ReleaseFundsTransferred
    : status === ERentDepositStatus.Released;

  if (releaseFundsTransferred) {
    return getLocalizedText("follow_up.rent_deposits_release.transferred");
  }

  if (releaseStatuses.includes(rentDeposit.status)) {
    return getLocalizedText("follow_up.rent_deposits_release.in_progress");
  }

  return getLocalizedText("follow_up.rent_deposits_release.not_initiated");
};

export const getPaymentOrderStatus = ({
  contract,
  paymentOrders,
}: {
  contract: IContract;
  paymentOrders?: IPaymentOrder[];
}) => {
  if (contract.manuallyCompletedPaymentRent) {
    return EStatus.Success;
  }

  if (!paymentOrders) {
    return EStatus.None;
  }

  if (paymentOrders.length === 0) {
    return EStatus.Error;
  }

  const hasRent = paymentOrders.find(
    paymentOrder => paymentOrder.type === EPaymentOrderType.Rent,
  );
  if (!hasRent) {
    return EStatus.Error;
  }

  const hasCommonCosts = paymentOrders.find(
    paymentOrder => paymentOrder.type === EPaymentOrderType.CommonCosts,
  );
  if (contract.currentCostsCommon && !hasCommonCosts) {
    return EStatus.Error;
  }

  const hasNonCommonCosts = paymentOrders.find(
    paymentOrder => paymentOrder.type === EPaymentOrderType.NonCommonCosts,
  );
  if (contract.currentCostsNonCommon && !hasNonCommonCosts) {
    return EStatus.Error;
  }

  return EStatus.Success;
};

export const getPaymentOrderTooltip = ({
  contract,
  paymentOrders,
}: {
  contract: IContract;
  paymentOrders?: IPaymentOrder[];
}) => {
  if (contract.manuallyCompletedPaymentRent) {
    return getLocalizedText(contract.manuallyCompletedPaymentRent);
  }

  if (!paymentOrders) {
    return undefined;
  }

  const checklist = [];

  const labelRent = ts.paymentOrderTypeLabel(EPaymentOrderType.Rent);
  const hasRent = paymentOrders.find(
    paymentOrder => paymentOrder.type === EPaymentOrderType.Rent,
  );

  checklist.push({ label: labelRent, success: !!hasRent });

  if (contract.currentCostsCommon) {
    const label = ts.paymentOrderTypeLabel(EPaymentOrderType.CommonCosts);
    const hasCommonCosts = paymentOrders.find(
      paymentOrder => paymentOrder.type === EPaymentOrderType.CommonCosts,
    );

    checklist.push({
      label,
      success: !!hasCommonCosts,
    });
  }

  if (contract.currentCostsNonCommon) {
    const label = ts.paymentOrderTypeLabel(EPaymentOrderType.NonCommonCosts);
    const hasNonCommonCosts = paymentOrders.find(
      paymentOrder => paymentOrder.type === EPaymentOrderType.NonCommonCosts,
    );

    checklist.push({
      label,
      success: !!hasNonCommonCosts,
    });
  }

  return renderPOTooltipChecklist(checklist);
};

export const getPaymentOrderV2Status = ({
  contract,
  paymentOrders,
}: {
  contract: IContract;
  paymentOrders?: IPaymentOrder[];
}) => {
  if (contract.manuallyCompletedPaymentRent) {
    return EStatus.Success;
  }

  if (!paymentOrders) {
    return EStatus.None;
  }

  if (paymentOrders.length === 0) {
    return EStatus.Error;
  }

  const hasRent = paymentOrders.find(
    paymentOrder => paymentOrder.type === EPaymentOrderType.Rent,
  );
  if (!hasRent) {
    return EStatus.Error;
  }

  const hasCommonCosts = paymentOrders.find(
    paymentOrder => paymentOrder.type === EPaymentOrderType.CommonCosts,
  );
  if (contract.currentCostsCommon && !hasCommonCosts) {
    return EStatus.Error;
  }

  const hasNonCommonCosts = paymentOrders.find(
    paymentOrder => paymentOrder.type === EPaymentOrderType.NonCommonCosts,
  );
  if (contract.currentCostsNonCommon && !hasNonCommonCosts) {
    return EStatus.Error;
  }

  return EStatus.Success;
};

export const getPaymentOrderV2Tooltip = ({
  contract,
  paymentOrders,
}: {
  contract: IContract;
  paymentOrders?: IPaymentOrder[];
}) => {
  if (contract.manuallyCompletedPaymentRent) {
    return getLocalizedText(contract.manuallyCompletedPaymentRent);
  }

  if (!paymentOrders) {
    return undefined;
  }

  const checklist = [];

  const labelRent = ts.paymentOrderTypeLabel(EPaymentOrderType.Rent);
  const hasRent = paymentOrders.find(
    paymentOrder => paymentOrder.type === EPaymentOrderType.Rent,
  );

  checklist.push({ label: labelRent, success: !!hasRent });

  if (contract.currentCostsCommon) {
    const label = ts.paymentOrderTypeLabel(EPaymentOrderType.CommonCosts);
    const hasCommonCosts = paymentOrders.find(
      paymentOrder => paymentOrder.type === EPaymentOrderType.CommonCosts,
    );

    checklist.push({
      label,
      success: !!hasCommonCosts,
    });
  }

  if (contract.currentCostsNonCommon) {
    const label = ts.paymentOrderTypeLabel(EPaymentOrderType.NonCommonCosts);
    const hasNonCommonCosts = paymentOrders.find(
      paymentOrder => paymentOrder.type === EPaymentOrderType.NonCommonCosts,
    );

    checklist.push({
      label,
      success: !!hasNonCommonCosts,
    });
  }

  return renderPOTooltipChecklist(checklist);
};

export const getOutstandingPaymentOrderStatus = ({
  contract,
  paymentOrders,
}: {
  contract: IContract;
  paymentOrders?: IPaymentOrder[];
}) => {
  if (contract.manuallyCompletedEndPaymentRent) {
    return EStatus.Success;
  }

  if (!paymentOrders || paymentOrders.length === 0) {
    return EStatus.Success;
  }

  return EStatus.Error;
};

export const getOutstandingPaymentOrderTooltip = ({
  contract,
  paymentOrders,
}: {
  contract: IContract;
  paymentOrders?: IPaymentOrder[];
}) => {
  if (contract.manuallyCompletedEndPaymentRent) {
    return getLocalizedText(contract.manuallyCompletedEndPaymentRent);
  }

  if (!paymentOrders || paymentOrders.length === 0) {
    return getLocalizedText("status.outstanding_payments.empty");
  }

  return getLocalizedText("status.outstanding_payments.open_payments");
};

export const getOutstandingPaymentOrderV2Status = ({
  contract,
  paymentOrders,
}: {
  contract: IContract;
  paymentOrders?: IPaymentOrder[];
}) => {
  if (contract.manuallyCompletedEndPaymentRent) {
    return EStatus.Success;
  }

  if (!paymentOrders || paymentOrders.length === 0) {
    return EStatus.Success;
  }

  return EStatus.Error;
};

export const getOutstandingPaymentOrderV2Tooltip = ({
  contract,
  paymentOrders,
}: {
  contract: IContract;
  paymentOrders?: IPaymentOrder[];
}) => {
  if (contract.manuallyCompletedEndPaymentRent) {
    return getLocalizedText(contract.manuallyCompletedEndPaymentRent);
  }

  if (!paymentOrders || paymentOrders.length === 0) {
    return getLocalizedText("status.outstanding_payments.empty");
  }

  return getLocalizedText("status.outstanding_payments.open_payments");
};

export const getPropertyInspectionReportStatus = ({
  contract,
}: {
  contract: IContract;
}) => {
  const {
    manuallyCompletedLocationDescription,
    locationDescriptionInRequestedAt,
  } = contract;

  if (manuallyCompletedLocationDescription) {
    return EStatus.Success;
  }

  if (locationDescriptionInRequestedAt) {
    return EStatus.Success;
  }

  return EStatus.Error;
};

export const getPropertyInspectionReportTooltip = ({
  contract,
}: {
  contract: IContract;
}) => {
  const {
    manuallyCompletedLocationDescription,
    locationDescriptionInRequestedAt,
    locationDescriptionOutRequestedAt,
  } = contract;

  if (manuallyCompletedLocationDescription) {
    return getLocalizedText(manuallyCompletedLocationDescription);
  }

  if (locationDescriptionInRequestedAt && locationDescriptionOutRequestedAt) {
    return ts.contractCardChecklistLocationDescriptionBothContent({
      values: {
        date: formatDate(contract.locationDescriptionOutRequestedAt!),
      },
    });
  }

  if (locationDescriptionInRequestedAt) {
    return ts.contractCardChecklistLocationDescriptionInContent({
      values: {
        date: formatDate(contract.locationDescriptionInRequestedAt!),
      },
    });
  }

  return getLocalizedText("status.location_description.empty");
};

export const getOutgoingPropertyInspectionReportStatus = ({
  contract,
}: {
  contract: IContract;
}) => {
  const {
    manuallyCompletedEndPropertyInspectionReport,
    locationDescriptionOutRequestedAt,
    locationDescriptionInRequestedAt,
  } = contract;

  if (manuallyCompletedEndPropertyInspectionReport) {
    return EStatus.Success;
  }

  if (locationDescriptionInRequestedAt === locationDescriptionOutRequestedAt) {
    return EStatus.Error;
  }

  if (locationDescriptionOutRequestedAt) {
    return EStatus.Success;
  }

  return EStatus.Error;
};

export const getOutgoingPropertyInspectionReportTooltip = ({
  contract,
}: {
  contract: IContract;
}) => {
  const {
    manuallyCompletedEndPropertyInspectionReport,
    locationDescriptionInRequestedAt,
    locationDescriptionOutRequestedAt,
  } = contract;

  if (manuallyCompletedEndPropertyInspectionReport) {
    return getLocalizedText(manuallyCompletedEndPropertyInspectionReport);
  }

  if (
    locationDescriptionInRequestedAt &&
    locationDescriptionOutRequestedAt &&
    locationDescriptionInRequestedAt === locationDescriptionOutRequestedAt
  ) {
    return getLocalizedText("status.outgoing_location_description.empty");
  }

  if (locationDescriptionOutRequestedAt) {
    return ts.contractCardChecklistLocationDescriptionOutContent({
      values: {
        date: formatDate(contract.locationDescriptionOutRequestedAt!),
      },
    });
  }

  return getLocalizedText("status.outgoing_location_description.empty");
};

export const getInsuranceStatus = ({
  contract,
  hasFeature,
}: {
  contract: IContract;
  hasFeature: boolean;
}) => {
  if (contract.manuallyCompletedInsurance) {
    return EStatus.Success;
  }

  if (!hasFeature) {
    return EStatus.Error;
  }

  switch (contract.insuranceExternalStatus || EInsuranceExternalStatus.None) {
    case EInsuranceExternalStatus.None:
    case EInsuranceExternalStatus.Failed:
      return EStatus.Error;

    case EInsuranceExternalStatus.Created:
    case EInsuranceExternalStatus.Pending:
      return EStatus.Warning;

    case EInsuranceExternalStatus.Completed:
      return EStatus.Success;

    default:
      return EStatus.None;
  }
};

export const getInsuranceTooltip = ({
  contract,
  hasFeature,
}: {
  contract: IContract;
  hasFeature: boolean;
}) => {
  if (contract.manuallyCompletedInsurance) {
    return getLocalizedText(contract.manuallyCompletedInsurance);
  }

  if (!hasFeature) {
    return getLocalizedText("system.unchecked");
  }

  const {
    insuranceExternalStatus = EInsuranceExternalStatus.None,
    insuranceLeadCompletedDate,
  } = contract;

  return getLocalizedText(
    `insurance.status.${insuranceExternalStatus}`.toLowerCase(),
    {
      date: insuranceLeadCompletedDate
        ? getFormattedDate(insuranceLeadCompletedDate)
        : "",
    },
  );
};

export const getRentioMoveServiceStatus = ({
  contract,
  hasFeature,
}: {
  contract: IContract;
  hasFeature: boolean;
}) => {
  if (contract.manuallyCompletedMove) {
    return EStatus.Success;
  }

  if (!hasFeature) {
    return EStatus.Error;
  }

  switch (contract.rentioMoveStatus || ERentioMoveServiceStatus.None) {
    default:
    case ERentioMoveServiceStatus.None:
      return EStatus.Error;

    case ERentioMoveServiceStatus.Processing:
      return EStatus.Warning;

    case ERentioMoveServiceStatus.Sent:
      return EStatus.Success;
  }
};

export const getRentioMoveServiceTooltip = ({
  contract,
  hasFeature,
}: {
  contract: IContract;
  hasFeature: boolean;
}) => {
  if (contract.manuallyCompletedMove) {
    return getLocalizedText(contract.manuallyCompletedMove);
  }

  if (!hasFeature) {
    return getLocalizedText("system.unchecked");
  }

  if (!contract) {
    return;
  }

  const { rentioMoveStatus } = contract;
  return getLocalizedText(
    `rentio_move_service.status.${
      rentioMoveStatus || ERentioMoveServiceStatus.None
    }`.toLowerCase(),
  );
};

export const getRegistrationStatus = ({
  contract,
  property,
  registration,
}: {
  contract: IContract;
  property: IProperty;
  registration?: IRegistration;
}) => {
  const { manuallyCompletedRegistration, rentRegistrationBrusselsStatus } =
    contract;

  if (manuallyCompletedRegistration) {
    return EStatus.Success;
  }

  const rentRegistrationDocuments = registration?.documents;

  const validForBrusselsRegistration = hasBrusselsRegistration(
    property,
    contract.leaseType,
  );

  const contractDocument = rentRegistrationDocuments?.find(
    document => document?.documentType === ERegistrationDocumentType.Contract,
  );

  const propertyInspectionDocument = rentRegistrationDocuments?.find(
    document =>
      document?.documentType ===
      ERegistrationDocumentType.PropertyInspectionReport,
  );

  const addendumDocument = rentRegistrationDocuments?.find(
    document => document?.documentType === ERegistrationDocumentType.Addendum,
  );

  const contractDocumentStatus =
    contractDocument?.status || ERegistrationDocumentStatus.None;
  const propertyInspectionDocumentStatus =
    propertyInspectionDocument?.status || ERegistrationDocumentStatus.None;

  const documentStatuses = [
    contractDocumentStatus,
    propertyInspectionDocumentStatus,
  ];

  if (validForBrusselsRegistration && rentRegistrationBrusselsStatus) {
    // @ts-ignore
    documentStatuses.push(rentRegistrationBrusselsStatus);
  }

  if (addendumDocument) {
    documentStatuses.push(addendumDocument?.status);
  }

  if (manuallyCompletedRegistration) {
    return EStatus.Success;
  }

  if (
    documentStatuses.every(
      status =>
        status &&
        [
          ERegistrationDocumentStatus.Registered,
          ERegistrationDocumentStatus.NotApplicable,
          ERegistrationDocumentStatus.RegisteredManually,
        ].includes(status),
    )
  ) {
    return EStatus.Success;
  }

  return EStatus.Error;
};

export const getRegistrationTooltip = ({
  contract,
  property,
  registration,
}: {
  contract: IContract;
  property: IProperty;
  registration?: IRegistration;
}) => {
  const { manuallyCompletedRegistration, rentRegistrationBrusselsStatus } =
    contract;

  if (manuallyCompletedRegistration) {
    return getLocalizedText(manuallyCompletedRegistration);
  }

  const rentRegistrationDocuments = registration?.documents;

  const contractDocument = rentRegistrationDocuments?.find(
    document => document?.documentType === ERegistrationDocumentType.Contract,
  );

  const propertyInspectionDocument = rentRegistrationDocuments?.find(
    document =>
      document?.documentType ===
      ERegistrationDocumentType.PropertyInspectionReport,
  );

  const addendumDocument = rentRegistrationDocuments?.find(
    document => document?.documentType === ERegistrationDocumentType.Addendum,
  );

  const rentRegistrationRentContractStatus =
    contractDocument?.status || ERegistrationDocumentStatus.None;
  const rentRegistrationLocationDescriptionStatus =
    propertyInspectionDocument?.status || ERegistrationDocumentStatus.None;
  const rentRegistrationAddendumStatus = addendumDocument?.status;

  const checklist = [];

  checklist.push({
    label: getLocalizedText("registration.status_info.contract", {
      status: getLocalizedText(
        `registration.status.${stringToSnakeCase(
          rentRegistrationRentContractStatus,
        )}`.toLowerCase(),
      ),
    }),
    success: [
      ERegistrationDocumentStatus.Registered,
      ERegistrationDocumentStatus.RegisteredManually,
      ERegistrationDocumentStatus.NotApplicable,
    ].includes(rentRegistrationRentContractStatus),
  });

  checklist.push({
    label: getLocalizedText("registration.status_info.location_description", {
      status: getLocalizedText(
        `registration.status.${stringToSnakeCase(
          rentRegistrationLocationDescriptionStatus,
        )}`.toLowerCase(),
      ),
    }),
    success: [
      ERegistrationDocumentStatus.Registered,
      ERegistrationDocumentStatus.RegisteredManually,
      ERegistrationDocumentStatus.NotApplicable,
    ].includes(rentRegistrationLocationDescriptionStatus),
  });

  if (
    hasBrusselsRegistration(property, contract.leaseType) &&
    rentRegistrationBrusselsStatus
  ) {
    checklist.push({
      label: getLocalizedText("registration.status_info.brussels", {
        status: getLocalizedText(
          `registration.status.${stringToSnakeCase(
            rentRegistrationBrusselsStatus,
          )}`.toLowerCase(),
        ),
      }),
      success: [
        ERegistrationBrusselsStatus.Registered,
        ERegistrationBrusselsStatus.RegisteredManually,
      ].includes(rentRegistrationBrusselsStatus),
    });
  }

  if (rentRegistrationAddendumStatus) {
    checklist.push({
      label: getLocalizedText("registration.status_info.addendum", {
        status: getLocalizedText(
          `registration.status.${stringToSnakeCase(
            rentRegistrationAddendumStatus,
          )}`.toLowerCase(),
        ),
      }),
      success: [
        ERegistrationDocumentStatus.Registered,
        ERegistrationDocumentStatus.RegisteredManually,
        ERegistrationDocumentStatus.NotApplicable,
      ].includes(rentRegistrationAddendumStatus),
    });
  }

  return renderTooltipChecklist(checklist);
};
